import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Calendar,
  ClipboardList,
  Mail,
  MessageSquareText,
  Phone,
  Plus,
  Tag,
  Users,
  Sparkles,
  Eye,
  MousePointerClick,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PilotCrmService } from '@/services/pilot-crm.service';
import { OperationsUserService } from '@/services/operations-user.service';
import { EmailTrackingService, EmailMessage } from '@/services/email-tracking.service';
import {
  EMPLOYEE_COUNT_RANGES,
  PILOT_LEAD_PRIORITIES,
  PILOT_LEAD_SOURCES,
  PILOT_LEAD_STATUSES,
  PILOT_TASK_TYPES,
} from '@/data/pilot-crm-constants';
import { PILOT_CRM_EMAIL_TEMPLATES } from '@/data/pilot-crm-email-templates';
import {
  EmployerLead,
  InteractionLog,
  LeadTask,
  LeadTaskStatus,
  LeadTaskType,
  PilotLeadPriority,
  PilotLeadSource,
  PilotLeadStatus,
} from '@/models/pilot-crm.model';
import {
  Badge,
  Button,
  Card,
  FormInput,
  FormSelect,
  FormTextarea,
  LoadingSpinner,
} from '@/components/common';
import { EnrichmentPanel } from '@/components/pilot-crm/EnrichmentPanel';
import { EmailComposeModal } from '@/components/pilot-crm/EmailComposeModal';

const isPilotCrmEditor = (role?: string | null) => {
  if (!role) return false;
  const normalized = role === 'operationsAdmin' ? 'operations_admin' : role;
  return ['operations_admin', 'executive', 'support'].includes(normalized);
};

const mergeTemplate = (text: string, lead: EmployerLead) => {
  const firstName = lead.contactName?.trim().split(' ')[0] || 'there';
  return text
    .replace(/{{CompanyName}}/g, lead.companyName || 'your company')
    .replace(/{{FirstName}}/g, firstName);
};

export const PilotCrmLeadDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const canEdit = isPilotCrmEditor(profile?.role);

  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'tasks' | 'enrichment'>('overview');
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [timelineFilter, setTimelineFilter] = useState<'all' | 'emails' | 'calls' | 'notes'>('all');
  const [expandedEmailId, setExpandedEmailId] = useState<string | null>(null);

  const { data: lead, isLoading } = useQuery({
    queryKey: ['pilot-crm-lead', id],
    queryFn: () => (id ? PilotCrmService.getLead(id) : null),
    enabled: !!id,
  });

  const { data: interactions = [] } = useQuery({
    queryKey: ['pilot-crm-interactions', id],
    queryFn: () => (id ? PilotCrmService.getInteractions(id) : []),
    enabled: !!id,
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ['pilot-crm-tasks', id],
    queryFn: () => (id ? PilotCrmService.getTasks(id) : []),
    enabled: !!id,
  });

  const { data: emailHistory = [] } = useQuery({
    queryKey: ['pilot-crm-email-history', id],
    queryFn: () => (id ? EmailTrackingService.getEmailHistory(id) : []),
    enabled: !!id,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['operations-users'],
    queryFn: () => OperationsUserService.getActiveUsers(),
    enabled: !!profile,
  });

  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((user) => map.set(user.id, user.fullName));
    return map;
  }, [users]);

  const [formData, setFormData] = useState<Partial<EmployerLead>>({});

  React.useEffect(() => {
    if (lead) {
      setFormData(lead);
    }
  }, [lead]);

  const updateLeadMutation = useMutation({
    mutationFn: async (updates: Partial<EmployerLead>) => {
      if (!id || !profile?.id) return;
      await PilotCrmService.updateLead(id, updates, profile.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', id] });
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-leads'] });
    },
  });

  const createNoteMutation = useMutation({
    mutationFn: async () => {
      if (!id || !profile?.id) return;
      await PilotCrmService.createInteraction(
        {
          leadId: id,
          type: 'note',
          channel: 'in_app',
          summary: noteText,
        },
        profile.id
      );
    },
    onSuccess: () => {
      setNoteText('');
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-interactions', id] });
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', id] });
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, status }: { taskId: string; status: LeadTaskStatus }) => {
      if (!profile?.id) return;
      await PilotCrmService.updateTask(taskId, { status }, profile.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-tasks', id] });
    },
  });

  if (isLoading || !lead) {
    return <LoadingSpinner text="Loading lead..." fullScreen />;
  }

  const openTasks = tasks.filter((task) => task.status === 'open');
  const completedTasks = tasks.filter((task) => task.status !== 'open');

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center justify-between">
        <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/pilot-crm')}>
          Back
        </Button>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/pilot-crm/tasks')}>
            View tasks
          </Button>
          <Button
            variant="outline"
            icon={MessageSquareText}
            onClick={() => canEdit && setShowInteractionModal(true)}
            disabled={!canEdit}
          >
            Log interaction
          </Button>
          <Button
            variant="outline"
            icon={Plus}
            onClick={() => canEdit && setShowTaskModal(true)}
            disabled={!canEdit}
          >
            Create task
          </Button>
          <Button
            variant="outline"
            icon={Mail}
            onClick={() => canEdit && setShowEmailModal(true)}
            disabled={!canEdit}
          >
            Email template
          </Button>
        </div>
      </div>

      <Card className="mb-6 border-l-4 border-red-400 bg-red-50">
        <p className="text-sm font-semibold text-red-700">Do not paste PHI/medical details here.</p>
      </Card>

      <Card className="mb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{lead.companyName}</h1>
            <p className="text-gray-600 mt-1">{lead.contactEmail}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Updated {format(lead.updatedAt, 'MMM d, yyyy')}
              </span>
              {lead.employeeCountRange && (
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {lead.employeeCountRange} employees
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 min-w-[280px]">
            <FormSelect
              label="Status"
              options={PILOT_LEAD_STATUSES.map((status) => ({
                value: status,
                label: status.replace('_', ' '),
              }))}
              value={lead.status}
              onChange={(e) =>
                canEdit && updateLeadMutation.mutate({ status: e.target.value as PilotLeadStatus })
              }
              disabled={!canEdit}
            />
            <FormSelect
              label="Priority"
              options={PILOT_LEAD_PRIORITIES.map((priority) => ({
                value: priority,
                label: priority,
              }))}
              value={lead.priority}
              onChange={(e) =>
                canEdit && updateLeadMutation.mutate({ priority: e.target.value as PilotLeadPriority })
              }
              disabled={!canEdit}
            />
            <FormSelect
              label="Owner"
              options={[
                { value: 'unassigned', label: 'Unassigned' },
                ...users.map((user) => ({ value: user.id, label: user.fullName })),
              ]}
              value={lead.ownerUserId || 'unassigned'}
              onChange={(e) =>
                canEdit &&
                updateLeadMutation.mutate({
                  ownerUserId: e.target.value === 'unassigned' ? undefined : e.target.value,
                })
              }
              disabled={!canEdit}
            />
          </div>
        </div>
      </Card>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
        <nav className="flex space-x-1 p-1">
          {(['overview', 'timeline', 'tasks', 'enrichment'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all ${activeTab === tab
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              {tab === 'overview' && <ClipboardList className="w-4 h-4" />}
              {tab === 'timeline' && <MessageSquareText className="w-4 h-4" />}
              {tab === 'tasks' && <Tag className="w-4 h-4" />}
              {tab === 'enrichment' && <Sparkles className="w-4 h-4" />}
              <span className="capitalize">{tab}</span>
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!canEdit) return;
              updateLeadMutation.mutate({
                companyName: formData.companyName,
                industry: formData.industry,
                employeeCountRange: formData.employeeCountRange,
                website: formData.website,
                hqState: formData.hqState,
                contactName: formData.contactName,
                contactEmail: formData.contactEmail,
                contactRole: formData.contactRole,
                phone: formData.phone,
                source: formData.source,
                tags: formData.tags || [],
                notes: formData.notes,
                nextActionAt: formData.nextActionAt,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                zipCode: formData.zipCode,
                rating: formData.rating,
                placeUrl: formData.placeUrl,
              });
            }}
            className="space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Company Name"
                value={formData.companyName || ''}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                required
                disabled={!canEdit}
              />
              <FormInput
                label="Industry"
                value={formData.industry || ''}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                disabled={!canEdit}
              />
              <FormSelect
                label="Employee Count"
                options={[
                  { value: '', label: 'Select range' },
                  ...EMPLOYEE_COUNT_RANGES.map((range) => ({ value: range, label: range })),
                ]}
                value={formData.employeeCountRange || ''}
                onChange={(e) =>
                  setFormData({ ...formData, employeeCountRange: e.target.value as any })
                }
                disabled={!canEdit}
              />
              <FormInput
                label="Website"
                value={formData.website || ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                disabled={!canEdit}
              />
              <FormInput
                label="City"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                disabled={!canEdit}
              />
              <FormInput
                label="State"
                value={formData.state || ''}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                disabled={!canEdit}
              />
              <FormInput
                label="Zip Code"
                value={formData.zipCode || ''}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                disabled={!canEdit}
              />
              <FormInput
                label="Address"
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                disabled={!canEdit}
                className="md:col-span-2"
              />
              <FormInput
                label="HQ State"
                value={formData.hqState || ''}
                onChange={(e) => setFormData({ ...formData, hqState: e.target.value })}
                disabled={!canEdit}
              />
              <FormInput
                label="Contact Name"
                value={formData.contactName || ''}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                disabled={!canEdit}
              />
              <FormInput
                label="Contact Email"
                type="email"
                value={formData.contactEmail || ''}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                required
                disabled={!canEdit}
              />
              <FormInput
                label="Contact Role"
                value={formData.contactRole || ''}
                onChange={(e) => setFormData({ ...formData, contactRole: e.target.value })}
                disabled={!canEdit}
              />
              <FormInput
                label="Phone"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={!canEdit}
              />
              <FormSelect
                label="Source"
                options={PILOT_LEAD_SOURCES.map((source) => ({
                  value: source,
                  label: source.replace('_', ' '),
                }))}
                value={formData.source || lead.source}
                onChange={(e) =>
                  setFormData({ ...formData, source: e.target.value as PilotLeadSource })
                }
                disabled={!canEdit}
              />
              <FormInput
                label="Next Action"
                type="datetime-local"
                value={
                  formData.nextActionAt
                    ? format(formData.nextActionAt, "yyyy-MM-dd'T'HH:mm")
                    : ''
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    nextActionAt: e.target.value ? new Date(e.target.value) : undefined,
                  })
                }
                disabled={!canEdit}
              />
              <FormInput
                label="Rating"
                type="number"
                step="0.1"
                value={formData.rating || ''}
                onChange={(e) =>
                  setFormData({ ...formData, rating: parseFloat(e.target.value) || undefined })
                }
                disabled={!canEdit}
              />
              <FormInput
                label="Google Place URL"
                value={formData.placeUrl || ''}
                onChange={(e) => setFormData({ ...formData, placeUrl: e.target.value })}
                disabled={!canEdit}
              />
            </div>
            <FormInput
              label="Tags"
              value={formData.tags?.join(', ') || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  tags: e.target.value.split(',').map((tag) => tag.trim()).filter(Boolean),
                })
              }
              disabled={!canEdit}
            />
            <FormTextarea
              label="Notes"
              rows={4}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              disabled={!canEdit}
            />
            {canEdit && (
              <div className="flex justify-end">
                <Button type="submit" variant="primary" isLoading={updateLeadMutation.isPending}>
                  Save changes
                </Button>
              </div>
            )}
          </form>
        </Card>
      )}

      {activeTab === 'timeline' && (
        <div className="space-y-6">
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <MessageSquareText className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Quick note</h3>
            </div>
            <FormTextarea
              label="Note"
              rows={3}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a short note to the timeline..."
              disabled={!canEdit}
            />
            <div className="flex justify-end mt-4">
              <Button
                variant="primary"
                onClick={() => createNoteMutation.mutate()}
                disabled={!canEdit || !noteText.trim()}
                isLoading={createNoteMutation.isPending}
              >
                Add note
              </Button>
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Timeline</h3>
              </div>
              <div className="flex items-center gap-2">
                {(['all', 'emails', 'calls', 'notes'] as const).map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setTimelineFilter(filter)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${timelineFilter === filter
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:bg-gray-100'
                      }`}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {interactions.length === 0 ? (
              <p className="text-sm text-gray-500">No interactions logged yet.</p>
            ) : (
              <div className="space-y-4">
                {interactions
                  .filter((interaction) => {
                    if (timelineFilter === 'all') return true;
                    if (timelineFilter === 'emails') return interaction.type.includes('email');
                    if (timelineFilter === 'calls') return interaction.type === 'call';
                    if (timelineFilter === 'notes') return interaction.type === 'note';
                    return true;
                  })
                  .map((interaction) => {
                    const emailMsg = interaction.type === 'email_sent'
                      ? emailHistory.find(e => e.subject === interaction.subject)
                      : null;
                    return (
                      <InteractionCard
                        key={interaction.id}
                        interaction={interaction}
                        ownerName={userNameById.get(interaction.createdBy) || interaction.createdBy}
                        emailMessage={emailMsg}
                        isExpanded={expandedEmailId === interaction.id}
                        onToggleExpand={() => setExpandedEmailId(expandedEmailId === interaction.id ? null : interaction.id)}
                      />
                    );
                  })}
              </div>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'enrichment' && (
        <EnrichmentPanel leadId={lead.id} />
      )}

      {activeTab === 'tasks' && (
        <div className="space-y-6">
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Open tasks</h3>
            </div>
            {openTasks.length === 0 ? (
              <p className="text-sm text-gray-500">No open tasks.</p>
            ) : (
              <div className="space-y-3">
                {openTasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    canEdit={canEdit}
                    onStatusChange={(status) => updateTaskMutation.mutate({ taskId: task.id, status })}
                  />
                ))}
              </div>
            )}
          </Card>
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Completed</h3>
            </div>
            {completedTasks.length === 0 ? (
              <p className="text-sm text-gray-500">No completed tasks yet.</p>
            ) : (
              <div className="space-y-3">
                {completedTasks.map((task) => (
                  <TaskRow key={task.id} task={task} canEdit={false} />
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {showInteractionModal && (
        <InteractionModal
          leadId={lead.id}
          onClose={() => setShowInteractionModal(false)}
          onSaved={() => {
            setShowInteractionModal(false);
            queryClient.invalidateQueries({ queryKey: ['pilot-crm-interactions', id] });
            queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', id] });
          }}
        />
      )}

      {showTaskModal && (
        <TaskModal
          leadId={lead.id}
          onClose={() => setShowTaskModal(false)}
          onSaved={() => {
            setShowTaskModal(false);
            queryClient.invalidateQueries({ queryKey: ['pilot-crm-tasks', id] });
            queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', id] });
          }}
        />
      )}

      {showEmailModal && (
        <EmailComposeModal
          lead={lead}
          onClose={() => setShowEmailModal(false)}
          onSent={() => {
            queryClient.invalidateQueries({ queryKey: ['pilot-crm-interactions', id] });
            queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', id] });
            queryClient.invalidateQueries({ queryKey: ['pilot-crm-email-history', id] });
          }}
        />
      )}
    </div>
  );
};

const InteractionCard: React.FC<{
  interaction: InteractionLog;
  ownerName: string;
  emailMessage?: EmailMessage | null;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}> = ({
  interaction,
  ownerName,
  emailMessage,
  isExpanded,
  onToggleExpand,
}) => {
    const getInteractionIcon = () => {
      switch (interaction.type) {
        case 'email_sent':
          return <Mail className="w-4 h-4 text-blue-500" />;
        case 'email_reply':
          return <Mail className="w-4 h-4 text-green-500" />;
        case 'call':
          return <Phone className="w-4 h-4 text-purple-500" />;
        case 'meeting':
          return <Users className="w-4 h-4 text-orange-500" />;
        default:
          return <MessageSquareText className="w-4 h-4 text-gray-500" />;
      }
    };

    return (
      <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getInteractionIcon()}
            <Badge variant="info" size="sm">
              {interaction.type.replace('_', ' ')}
            </Badge>
            <span className="text-sm text-gray-500">{interaction.channel}</span>
            {emailMessage && (
              <div className="flex items-center gap-1 ml-2">
                {emailMessage.openedAt && (
                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                    <Eye className="w-3 h-3" />
                    Opened
                  </span>
                )}
                {emailMessage.clickedAt && (
                  <span className="flex items-center gap-1 text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                    <MousePointerClick className="w-3 h-3" />
                    Clicked
                  </span>
                )}
                {emailMessage.bouncedAt && (
                  <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                    <AlertCircle className="w-3 h-3" />
                    Bounced
                  </span>
                )}
              </div>
            )}
          </div>
          <span className="text-xs text-gray-500">{format(interaction.createdAt, 'MMM d, yyyy p')}</span>
        </div>
        {interaction.subject && (
          <p
            className={`mt-2 font-semibold text-gray-900 ${onToggleExpand ? 'cursor-pointer hover:text-blue-600' : ''}`}
            onClick={onToggleExpand}
          >
            {interaction.subject}
          </p>
        )}
        <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">{interaction.summary}</p>
        {isExpanded && interaction.raw && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Full Content</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{interaction.raw}</p>
          </div>
        )}
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-gray-500">Logged by {ownerName}</p>
          {interaction.raw && onToggleExpand && (
            <button
              className="text-xs text-blue-600 hover:text-blue-700"
              onClick={onToggleExpand}
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>
    );
  };

const TaskRow: React.FC<{
  task: LeadTask;
  canEdit: boolean;
  onStatusChange?: (status: LeadTaskStatus) => void;
}> = ({ task, canEdit, onStatusChange }) => {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border border-gray-200 rounded-lg p-4">
      <div>
        <p className="font-semibold text-gray-900">{task.title}</p>
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
          <span>{task.type.replace('_', ' ')}</span>
          <span>Due {format(task.dueAt, 'MMM d, yyyy')}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={task.status === 'done' ? 'success' : 'gray'} size="sm">
          {task.status}
        </Badge>
        {canEdit && onStatusChange && (
          <>
            {task.status !== 'done' && (
              <Button variant="outline" size="sm" onClick={() => onStatusChange('done')}>
                Mark done
              </Button>
            )}
            {task.status !== 'canceled' && (
              <Button variant="ghost" size="sm" onClick={() => onStatusChange('canceled')}>
                Cancel
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const InteractionModal: React.FC<{
  leadId: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ leadId, onClose, onSaved }) => {
  const { profile } = useAuth();
  const [formData, setFormData] = useState({
    type: 'call',
    channel: 'phone',
    subject: '',
    summary: '',
    raw: '',
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error('Missing user profile');
      await PilotCrmService.createInteraction(
        {
          leadId,
          type: formData.type as any,
          channel: formData.channel as any,
          subject: formData.subject || undefined,
          summary: formData.summary,
          raw: formData.raw || undefined,
        },
        profile.id
      );
    },
    onSuccess: onSaved,
    onError: (err: any) => setError(err?.message || 'Failed to log interaction'),
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <Card className="relative max-w-xl w-full z-10">
          <div className="flex items-center gap-2 mb-4">
            <Phone className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Log interaction</h2>
          </div>
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-3 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              mutation.mutate();
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormSelect
                label="Type"
                options={[
                  { value: 'email_sent', label: 'Email sent' },
                  { value: 'email_reply', label: 'Email reply' },
                  { value: 'call', label: 'Call' },
                  { value: 'meeting', label: 'Meeting' },
                  { value: 'note', label: 'Note' },
                ]}
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              />
              <FormSelect
                label="Channel"
                options={[
                  { value: 'email', label: 'Email' },
                  { value: 'phone', label: 'Phone' },
                  { value: 'zoom', label: 'Zoom' },
                  { value: 'in_app', label: 'In app' },
                ]}
                value={formData.channel}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
              />
            </div>
            <FormInput
              label="Subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            />
            <FormTextarea
              label="Summary"
              rows={3}
              required
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
            />
            <FormTextarea
              label="Raw (optional)"
              rows={2}
              value={formData.raw}
              onChange={(e) => setFormData({ ...formData, raw: e.target.value })}
            />
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" isLoading={mutation.isPending}>
                Save
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

const TaskModal: React.FC<{
  leadId: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ leadId, onClose, onSaved }) => {
  const { profile } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    type: 'follow_up' as LeadTaskType,
    dueAt: '',
    autoExecute: false,
    templateId: '',
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error('Missing user profile');
      await PilotCrmService.createTask(
        {
          leadId,
          title: formData.title,
          type: formData.type,
          dueAt: new Date(formData.dueAt),
          autoExecute: formData.autoExecute,
          templateId: formData.autoExecute ? formData.templateId : undefined,
        },
        profile.id
      );
    },
    onSuccess: onSaved,
    onError: (err: any) => setError(err?.message || 'Failed to create task'),
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <Card className="relative max-w-lg w-full z-10">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Create task</h2>
          </div>
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-3 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              mutation.mutate();
            }}
            className="space-y-4"
          >
            <FormInput
              label="Title"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
            <FormSelect
              label="Type"
              options={PILOT_TASK_TYPES.map((type) => ({
                value: type,
                label: type.replace('_', ' '),
              }))}
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as LeadTaskType })}
            />
            <FormInput
              label="Due At"
              type="datetime-local"
              required
              value={formData.dueAt}
              onChange={(e) => setFormData({ ...formData, dueAt: e.target.value })}
            />

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoExecute"
                checked={formData.autoExecute}
                onChange={(e) => setFormData({ ...formData, autoExecute: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="autoExecute" className="text-sm text-gray-700">
                Auto-execute at due time (sends email)
              </label>
            </div>

            {formData.autoExecute && (
              <FormSelect
                label="Email Template"
                options={PILOT_CRM_EMAIL_TEMPLATES.map((t) => ({
                  value: t.id,
                  label: t.name,
                }))}
                value={formData.templateId}
                onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                required
              />
            )}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" isLoading={mutation.isPending}>
                Create task
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

const EmailTemplateModal: React.FC<{
  lead: EmployerLead;
  onClose: () => void;
  onMarkedSent: (subject: string, body: string) => Promise<void>;
}> = ({ lead, onClose, onMarkedSent }) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState(PILOT_CRM_EMAIL_TEMPLATES[0].id);
  const [isCopying, setIsCopying] = useState(false);
  const [isMarking, setIsMarking] = useState(false);

  const selectedTemplate =
    PILOT_CRM_EMAIL_TEMPLATES.find((template) => template.id === selectedTemplateId) ||
    PILOT_CRM_EMAIL_TEMPLATES[0];

  const subject = mergeTemplate(selectedTemplate.subject, lead);
  const body = mergeTemplate(selectedTemplate.body, lead);

  const copyToClipboard = async () => {
    setIsCopying(true);
    await navigator.clipboard.writeText(`${subject}\n\n${body}`);
    setIsCopying(false);
  };

  const markAsSent = async () => {
    setIsMarking(true);
    await onMarkedSent(subject, body);
    setIsMarking(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <Card className="relative max-w-2xl w-full z-10">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Email template</h2>
          </div>
          <FormSelect
            label="Template"
            options={PILOT_CRM_EMAIL_TEMPLATES.map((template) => ({
              value: template.id,
              label: template.name,
            }))}
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
          />
          <div className="mt-4 space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase">Subject</p>
              <p className="text-sm font-semibold text-gray-900">{subject}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase">Body</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                {body}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-200 mt-4">
            <Button variant="outline" onClick={copyToClipboard} isLoading={isCopying}>
              Copy to clipboard
            </Button>
            <Button variant="primary" onClick={markAsSent} isLoading={isMarking}>
              Mark as sent
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};
